/**
 * 로그인/회원가입 추적 훅
 * 
 * 로그인 또는 회원가입 프로세스를 추적
 * - auth_* 이벤트는 로그인과 회원가입을 모두 포함하는 통합 이벤트
 * - start()는 세션 스토리지를 확인하여 저장된 의도가 있으면 자동 복원
 * - 부모 의도 자동 연결 지원 (parentIntentType)
 * 
 * @example
 * ```tsx
 * // 로그인 페이지
 * function LoginPage() {
 *   const auth = useAuth();
 *   const router = useRouter();
 *   
 *   useEffect(() => {
 *     // 페이지 진입 시 auth 시작
 *     auth.start({
 *       ui: 'page',
 *       entry_reason: router.query.returnTo ? 'gate' : 'direct',
 *       method_kind: 'password',  // 일반 로그인 폼
 *       mode_pred: 'login',
 *       provider: null,
 *       source_key: 'login_page',
 *       return_to: router.query.returnTo as string,
 *     });
 *   }, []);
 *   
 *   const handleLogin = async (email: string, password: string) => {
 *     try {
 *       const result = await apiLogin(email, password);
 *       auth.success({
 *         method_kind: 'password',
 *         provider: null,
 *         mode_resolved: 'login',
 *       });
 *       router.push(router.query.returnTo || '/');
 *     } catch (error) {
 *       auth.fail('invalid_credentials');
 *     }
 *   };
 *   
 *   const handleKakaoLogin = async () => {
 *     // 소셜 로그인 시작
 *     auth.start({
 *       ui: 'page',
 *       entry_reason: 'direct',
 *       method_kind: 'oauth',
 *       mode_pred: 'unknown',  // 소셜은 로그인인지 회원가입인지 모름
 *       provider: 'kakao',
 *       source_key: 'login_page_kakao',
 *     });
 *     // 카카오 리다이렉트...
 *   };
 * }
 * 
 * // 소셜 로그인 콜백
 * function SocialCallback() {
 *   const auth = useAuth();
 *   
 *   useEffect(() => {
 *     const handleCallback = async () => {
 *       try {
 *         const result = await processOAuthCallback();
 *         auth.success({
 *           method_kind: 'oauth',
 *           provider: 'kakao',
 *           mode_resolved: result.isNewUser ? 'signup' : 'login',
 *         });
 *       } catch (error) {
 *         auth.fail('oauth_error');
 *       }
 *     };
 *     handleCallback();
 *   }, []);
 * }
 * ```
 */

import { useCallback, useRef } from 'react';
import { track } from '../../../core/client';
import { generateIntentId, setIntentId, clearIntentId, getIntentId } from '../../../core/ids';

export interface AuthStartContext {
    ui: 'bottom_sheet' | 'page';
    entry_reason: 'gate' | 'direct';
    method_kind: 'oauth' | 'password';
    mode_pred: 'login' | 'signup' | 'unknown';
    provider: 'kakao' | 'google' | null;
    source_key: string;
    return_to?: string;
}

export interface AuthOutcomeContext {
    method_kind: 'oauth' | 'password';
    provider: 'kakao' | 'google' | null;
    mode_resolved: 'login' | 'signup';
}

export interface AuthActions {
    start: (context: AuthStartContext, options?: { parentIntentId?: string; parentIntentType?: string }) => void;
    success: (context: AuthOutcomeContext) => void;
    fail: (context: AuthOutcomeContext, reason?: string) => void;
}

/**
 * 로그인/회원가입 추적 훅
 * 
 * Auth는 success/fail 시점에 method_kind, provider, mode_resolved를 알 수 있으므로
 * 별도로 구현합니다.
 */
export function useAuth(): AuthActions {
    const currentIntentIdRef = useRef<string | null>(null);
    const startTimeRef = useRef<number | null>(null);

    /**
     * Auth 의도 시작
     */
    const start = useCallback((
        context: AuthStartContext,
        options?: { parentIntentId?: string; parentIntentType?: string }
    ) => {
        // 기존 의도 확인
        const stored = getIntentId('auth');
        if (stored) {
            currentIntentIdRef.current = stored.intentId;
            return stored.intentId;
        }

        // 새 의도 생성
        const intentId = generateIntentId();
        currentIntentIdRef.current = intentId;
        startTimeRef.current = Date.now();

        // 부모 의도 ID 결정
        let parentIntentId = options?.parentIntentId;
        if (options?.parentIntentType && !parentIntentId) {
            const parentStored = getIntentId(options.parentIntentType);
            if (parentStored) {
                parentIntentId = parentStored.intentId;
            }
        }

        setIntentId('auth', intentId, context);
        track('auth_start', {
            intent_id: intentId,
            ui: context.ui,
            entry_reason: context.entry_reason,
            method_kind: context.method_kind,
            mode_pred: context.mode_pred,
            provider: context.provider,
            source_key: context.source_key,
            return_to: context.return_to,
            parent_intent_id: parentIntentId,
            required_gate: 'none',
        });

        return intentId;
    }, []);

    /**
     * Auth 성공
     */
    const success = useCallback((outcomeContext: AuthOutcomeContext) => {
        if (!currentIntentIdRef.current) {
            console.warn('⚠️ [Auth] No active intent to mark as success');
            return;
        }

        const latencyMs = startTimeRef.current ? Date.now() - startTimeRef.current : undefined;

        track('auth_success', {
            intent_id: currentIntentIdRef.current,
            // method_kind: outcomeContext.method_kind,
            // provider: outcomeContext.provider,
            // mode_resolved: outcomeContext.mode_resolved,
            latency_ms: latencyMs,
        });

        console.log(`✅ [Auth] Success: auth (${currentIntentIdRef.current})`);

        // 정리
        clearIntentId('auth');
        currentIntentIdRef.current = null;
        startTimeRef.current = null;
    }, []);

    /**
     * Auth 실패
     */
    const fail = useCallback((outcomeContext: AuthOutcomeContext, reason?: string) => {
        if (!currentIntentIdRef.current) {
            console.warn('⚠️ [Auth] No active intent to mark as fail');
            return;
        }

        track('auth_fail', {
            intent_id: currentIntentIdRef.current,
            // method_kind: outcomeContext.method_kind,
            // provider: outcomeContext.provider,
            // mode_resolved: outcomeContext.mode_resolved,
            fail_reason: reason,
        });


        // 정리
        clearIntentId('auth');
        currentIntentIdRef.current = null;
        startTimeRef.current = null;
    }, []);

    return {
        start,
        success,
        fail,
    };
}

